// Shared copy + behavior for inviting friends to a Chipless room.
// Used by the in-game Invite pop-up and the host's pre-game lobby.

import { t } from '../i18n';

export const INVITE_SHARE_TITLE = 'Join my poker game';
export const INVITE_SHARE_TEXT = 'Your chips are on your phone tonight 🃏 Join the game:';

function shareText() {
  return t('share.text');
}

// Payload for navigator.share (lobby + in-game invite).
// iOS Copy in the share sheet copies `text` (and drops `url`) when both
// are set — guests used to get the pitch without a join link. Putting the
// sentence + URL in `text` only means Messages/SMS is not a bare link and
// Copy still includes the URL. Title and `url` stay off the payload.
// Dedicated Copy link buttons use the clipboard API with the URL only.
// Rejoin / Table screen keep `{ url }` via shareLink — they are not invites.
export function buildInviteSharePayload({ url }) {
  const text = shareText();
  return { text: url ? `${text} ${url}` : text };
}

export function rejoinUrlForSeat(roomCode, token, origin) {
  const code = String(roomCode || '').trim().toUpperCase();
  const tok = String(token || '').trim();
  if (!code || !tok) return '';
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return base ? `${base}/r/${code}/${tok}` : `/r/${code}/${tok}`;
}

export function displayRejoinUrl(url) {
  return String(url || '').replace(/^https?:\/\//, '');
}

export function tvUrlForRoom(roomCode, origin) {
  const code = String(roomCode || '').trim().toUpperCase();
  if (!code) return '';
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return base ? `${base}/tv/${code}` : `/tv/${code}`;
}

/** Brand display — always playchipless.com, matching the approved sheet. */
export function displayTvUrl(roomCode) {
  const code = String(roomCode || '').trim().toUpperCase();
  return code ? `playchipless.com/tv/${code}` : '';
}

// Clipboard fallback — the message with the link appended after a space.
export function buildInviteClipboardText({ url }) {
  const text = shareText();
  return url ? `${text} ${url}` : text;
}

export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for non-secure contexts / older mobile browsers
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
}

export function canNativeShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Share the invite. Uses navigator.share when available; otherwise (or if the
 * share sheet fails for a reason other than the user cancelling) copies the
 * message plus link to the clipboard.
 * Resolves to 'shared' | 'copied' | 'cancelled'.
 */
export async function shareInvite({ url }) {
  if (canNativeShare()) {
    try {
      await navigator.share(buildInviteSharePayload({ url }));
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
    }
  }
  await copyText(buildInviteClipboardText({ url }));
  return 'copied';
}

/** Rejoin / Table screen: `{ url }` only so iOS Copy yields the link. */
export async function shareLink({ url }) {
  if (canNativeShare()) {
    try {
      await navigator.share({ url });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
    }
  }
  await copyText(url);
  return 'copied';
}
